import { Popover } from 'antd';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import styled, { css } from 'styled-components';

import { PANORAMA_STEP_DISTANCES, PANORAMA_STEP_MODES } from '../../constants/game';
import { PanoramaStepMode, setPanoramaStepMode } from '../../redux/actions/settings';

const TOUR_STORAGE_KEY = 'kdetosakra_step_slider_tour_seen_v1';

const tokens = {
    surface: 'rgba(18, 18, 20, 0.92)',
    surfaceHover: 'rgba(28, 28, 32, 0.95)',
    border: 'rgba(255, 255, 255, 0.08)',
    text: '#ffffff',
    textMuted: 'rgba(255, 255, 255, 0.6)',
    accent: '#c80707',
    activeBg: 'linear-gradient(135deg, #c80707 0%, #a00606 100%)',
    blur: '20px',
    radius: '12px',
    shadow: '0 8px 32px rgba(0, 0, 0, 0.4), 0 2px 8px rgba(0, 0, 0, 0.2)',
};

const Wrapper = styled.div<{ $disabled?: boolean }>`
    position: absolute;
    top: 16px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 11;
    pointer-events: ${props => (props.$disabled ? 'none' : 'auto')};
    opacity: ${props => (props.$disabled ? 0.5 : 1)};
    transition: opacity 0.2s ease;
`;

const Card = styled.div`
    background: ${tokens.surface};
    backdrop-filter: blur(${tokens.blur});
    -webkit-backdrop-filter: blur(${tokens.blur});
    border: 1px solid ${tokens.border};
    border-radius: ${tokens.radius};
    box-shadow: ${tokens.shadow};
    padding: 8px 12px 10px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;

    @media (max-width: 600px) {
        padding: 6px 8px 8px;
    }
`;

const Label = styled.div`
    font-family: 'Work Sans', sans-serif;
    font-size: 10px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: ${tokens.textMuted};

    @media (max-width: 600px) {
        font-size: 9px;
    }
`;

const SegmentedGroup = styled.div`
    display: inline-flex;
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid ${tokens.border};
    border-radius: 8px;
    padding: 3px;
    gap: 2px;
`;

const Segment = styled.button<{ $active?: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: 0;
    background: transparent;
    color: ${tokens.textMuted};
    font-family: 'Work Sans', sans-serif;
    font-size: 12px;
    font-weight: 600;
    padding: 6px 10px;
    min-width: 56px;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    user-select: none;

    &:hover:not(:disabled) {
        color: ${tokens.text};
        background: rgba(255, 255, 255, 0.06);
    }

    &:disabled {
        cursor: not-allowed;
    }

    ${props =>
        props.$active &&
        css`
            background: ${tokens.activeBg};
            color: ${tokens.text};
            box-shadow: 0 2px 8px rgba(200, 7, 7, 0.4);

            &:hover:not(:disabled) {
                background: ${tokens.activeBg};
                color: ${tokens.text};
            }
        `}

    @media (max-width: 600px) {
        font-size: 11px;
        padding: 5px 8px;
        min-width: 40px;
        gap: 4px;
    }
`;

/**
 * Diskrétní keycap badge — vizuální nápověda klávesové zkratky (1–4).
 * Nenápadný (snížená opacity, monospace, tmavé pozadí), ale viditelný i v active stavu.
 * Na mobilu skrytý — touch device nemá klávesnici.
 */
const KeyHint = styled.span<{ $active?: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 14px;
    height: 14px;
    padding: 0 3px;
    border-radius: 3px;
    background: ${props => (props.$active ? 'rgba(0, 0, 0, 0.28)' : 'rgba(255, 255, 255, 0.08)')};
    border: 1px solid ${props => (props.$active ? 'rgba(255, 255, 255, 0.25)' : 'rgba(255, 255, 255, 0.14)')};
    color: ${props => (props.$active ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.55)')};
    font-family: 'JetBrains Mono', 'SF Mono', 'Consolas', monospace;
    font-size: 9px;
    font-weight: 600;
    line-height: 1;
    box-shadow: inset 0 -1px 0 rgba(0, 0, 0, 0.25);
    pointer-events: none;

    @media (max-width: 600px) {
        display: none;
    }
`;

const TourBox = styled.div`
    max-width: 280px;
    font-family: 'Work Sans', sans-serif;
    font-size: 13px;
    line-height: 1.5;
    color: #1f1f1f;
`;

const TourTitle = styled.div`
    font-weight: 700;
    font-size: 14px;
    margin-bottom: 6px;
`;

const TourActions = styled.div`
    margin-top: 10px;
    display: flex;
    justify-content: flex-end;
`;

const TourButton = styled.button`
    background: #c80707;
    color: #fff;
    border: 0;
    border-radius: 6px;
    padding: 5px 14px;
    font-family: 'Work Sans', sans-serif;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;

    &:hover {
        background: #a00606;
    }
`;

interface SegmentDef {
    mode: PanoramaStepMode;
    label: string;
    labelMobile: string;
    hotkey: string;
}

const SEGMENTS: SegmentDef[] = [
    {
        mode: PANORAMA_STEP_MODES.DEFAULT as PanoramaStepMode,
        label: 'VÝCHOZÍ',
        labelMobile: 'NA',
        hotkey: '1',
    },
    {
        mode: PANORAMA_STEP_MODES.STEP_1 as PanoramaStepMode,
        label: `${PANORAMA_STEP_DISTANCES[PANORAMA_STEP_MODES.STEP_1]} m`,
        labelMobile: `${PANORAMA_STEP_DISTANCES[PANORAMA_STEP_MODES.STEP_1]}`,
        hotkey: '2',
    },
    {
        mode: PANORAMA_STEP_MODES.STEP_2 as PanoramaStepMode,
        label: `${PANORAMA_STEP_DISTANCES[PANORAMA_STEP_MODES.STEP_2]} m`,
        labelMobile: `${PANORAMA_STEP_DISTANCES[PANORAMA_STEP_MODES.STEP_2]}`,
        hotkey: '3',
    },
    {
        mode: PANORAMA_STEP_MODES.STEP_3 as PanoramaStepMode,
        label: `${PANORAMA_STEP_DISTANCES[PANORAMA_STEP_MODES.STEP_3]} m`,
        labelMobile: `${PANORAMA_STEP_DISTANCES[PANORAMA_STEP_MODES.STEP_3]}`,
        hotkey: '4',
    },
];

interface PanoramaStepModeSwitcherProps {
    disabled?: boolean;
}

export const PanoramaStepModeSwitcher: React.FC<PanoramaStepModeSwitcherProps> = ({ disabled = false }) => {
    const dispatch = useDispatch();
    const isMobileViewport = useMediaQuery({ query: '(max-width: 600px)' });

    const currentMode = useSelector(
        (state: any) => state.settings?.panoramaStepMode ?? PANORAMA_STEP_MODES.DEFAULT,
    ) as PanoramaStepMode;

    const handleSelect = useCallback(
        (mode: PanoramaStepMode) => {
            if (disabled || mode === currentMode) return;
            dispatch(setPanoramaStepMode(mode));
        },
        [disabled, currentMode, dispatch],
    );

    // ===== Hotkeys 1–4 =====
    useEffect(() => {
        if (disabled) return;

        const handler = (e: KeyboardEvent) => {
            const target = e.target as HTMLElement | null;
            if (target) {
                const tag = target.tagName;
                if (tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable) return;
            }
            // ignore if any modifier key is held — necháme nativní zkratky
            if (e.metaKey || e.ctrlKey || e.altKey) return;

            const seg = SEGMENTS.find(s => s.hotkey === e.key);
            if (!seg) return;

            e.preventDefault();
            handleSelect(seg.mode);
        };

        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [disabled, handleSelect]);

    // ===== First-time tour =====
    const [tourVisible, setTourVisible] = useState(false);
    const tourCheckedRef = useRef(false);

    useEffect(() => {
        if (tourCheckedRef.current) return;
        tourCheckedRef.current = true;

        try {
            const seen = localStorage.getItem(TOUR_STORAGE_KEY);
            if (!seen) {
                // Drobné zpoždění, ať se UI ustálí a Popover správně přilepí
                const t = setTimeout(() => setTourVisible(true), 600);
                return () => clearTimeout(t);
            }
        } catch {
            // localStorage nedostupný (privátní mode atd.) — tour přeskočíme
        }
    }, []);

    const dismissTour = useCallback(() => {
        setTourVisible(false);
        try {
            localStorage.setItem(TOUR_STORAGE_KEY, '1');
        } catch {
            // ignore
        }
    }, []);

    const tourContent = useMemo(
        () => (
            <TourBox>
                <TourTitle>Rychlost pohybu v panoramatu</TourTitle>
                <div>
                    Vyber, jak velký skok udělá jeden klik na šipku v panoramatu.
                    <br />
                    <strong>VÝCHOZÍ</strong> = výchozí pohyb po jednom panoramatu (jako doteď).
                    <br />
                    <strong>50 / 100 / 200&nbsp;m</strong> = vlastní fast jump pro rychlejší průzkum. Tip: klávesy{' '}
                    <strong>1–4</strong> přepínají rychlost, <strong>↑/↓</strong> nebo <strong>W/S</strong> jdou tam a
                    zpět.
                </div>
                <TourActions>
                    <TourButton type="button" onClick={dismissTour}>
                        Rozumím
                    </TourButton>
                </TourActions>
            </TourBox>
        ),
        [dismissTour],
    );

    return (
        <Popover
            content={tourContent}
            open={tourVisible && !disabled}
            placement="bottom"
            trigger={[]}
            onOpenChange={open => {
                if (!open) dismissTour();
            }}
        >
            <Wrapper $disabled={disabled}>
                <Card>
                    <Label>Rychlost pohybu</Label>
                    <SegmentedGroup role="radiogroup" aria-label="Rychlost pohybu v panoramatu">
                        {SEGMENTS.map(seg => {
                            const labelText = isMobileViewport ? seg.labelMobile : seg.label;
                            const isActive = currentMode === seg.mode;
                            return (
                                <Segment
                                    key={seg.mode}
                                    type="button"
                                    role="radio"
                                    aria-checked={isActive}
                                    $active={isActive}
                                    disabled={disabled}
                                    onClick={() => handleSelect(seg.mode)}
                                >
                                    <span>{labelText}</span>
                                    <KeyHint $active={isActive} aria-hidden>
                                        {seg.hotkey}
                                    </KeyHint>
                                </Segment>
                            );
                        })}
                    </SegmentedGroup>
                </Card>
            </Wrapper>
        </Popover>
    );
};

export default PanoramaStepModeSwitcher;
